import mapboxgl from "mapbox-gl";

import { MapboxStyleLayer } from "fond/types";

type NestedGetter = ["get", string] | ["get", string, NestedGetter];

// Create a mapbox expression that gets the given path from the current feature.
const getNested = (path: string[]) => {
  if (path.length === 0) {
    throw new Error("Cannot get empty path");
  }
  return path.slice(1).reduce<NestedGetter>((expression, attribute) => ["get" as const, attribute, expression], ["get" as const, path.at(0)!]);
};

const dropAssignmentStyles: MapboxStyleLayer[] = [
  {
    id: "gl-draw-line-preview",
    type: "line" as const,
    filter: [
      "all",
      ["==", "$type", "LineString"],
      ["==", "user_type", "assignmentCablePreview"],
      ["==", "draw_mode", "drawAssignment"],
      ["!=", "hidden", true],
    ],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-width": 2,
      "line-color": [
        "case",
        // The preview feature might not have a source property if the user's mouse isn't near a source.
        ["has", "user_source"],
        getNested(["user_source", "properties", "color"]),
        getNested(["user_originalCable", "properties", "source", "properties", "color"]),
      ],
      "line-dasharray": [2, 2],
      "line-opacity": 0.9,
      // The preview line often overlaps with existing cable paths making it hard to see; add a slight offset
      // so that the user can actually see the new assignment.
      "line-offset": 4,
    },
  },
  {
    id: "gl-draw-line",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["!=", "user_type", "assignmentCablePreview"], ["!=", "hidden", true]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-width": 2,
      "line-color": getNested(["user_source", "properties", "color"]),
      "line-dasharray": ["case", ["==", ["get", "user_type"], "assignmentCable"], ["literal", [2, 2]], ["literal", [1, 0]]],
      "line-opacity": 0.9,
    },
  },
  {
    id: "gl-draw-service-location-group-fibre-count",
    type: "symbol",
    filter: ["all", ["==", "$type", "Point"], ["==", "user_type", "sinkGroup"], ["!=", "hidden", true]],
    layout: {
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "text-field": ["get", "user_fibreCount"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 10, 10, 16, 12],
    },
    paint: {
      "text-opacity": 1,
      "text-halo-width": 2,
      "text-halo-color": "#ffffff",
    },
  },
];

/**
 * Additional styles for sources.
 * Sources are rendered as icon symbols and should appear on top of cables.
 */
const sourceStyles: MapboxStyleLayer[] = [
  {
    id: "gl-draw-point-source",
    filter: [
      "all",
      ["==", ["get", "user_type"], "source"],
      [">=", ["coalesce", ["get", "user_remainingCapacity"], 0], 0],
      ["!=", ["get", "hidden"], true],
    ],
    type: "symbol",
    layout: {
      "text-field": "\uE882",
      "text-anchor": "bottom",
      "text-justify": "center",
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "text-font": ["fontello Regular"],
      "text-offset": [0.05, 0],
      "text-padding": 0,
      "text-rotate": 0.01,
      "text-size": ["interpolate", ["linear"], ["zoom"], 12, 0, 18, 22],
      "text-line-height": -0.45,
    },
    paint: {
      "text-opacity": 1,
      "text-translate": [-0.6, 0.1],
      "text-halo-width": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 2],
      "text-halo-color": "#ffffff",
      "text-color": "rgba(237, 28, 36, 1)",
    },
  } as mapboxgl.SymbolLayer,
  {
    id: "gl-draw-over-capacity-source",
    type: "symbol",
    filter: [
      "all",
      ["==", ["get", "user_type"], "source"],
      ["<", ["coalesce", ["get", "user_remainingCapacity"], 0], 0],
      ["!=", ["get", "hidden"], true],
    ],
    layout: {
      "text-field": "\uE81E",
      "text-anchor": "bottom",
      "text-justify": "center",
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "text-font": ["fontello Regular"],
      "text-offset": [0.05, 0],
      "text-padding": 0,
      "text-rotate": 0.01,
      "text-size": ["interpolate", ["linear"], ["zoom"], 12, 0, 18, 22],
      "text-line-height": -0.45,
      "text-rotation-alignment": "viewport",
    },
    paint: {
      "text-color": "yellow",
      "text-translate": [-0.6, 0.1],
      "text-halo-color": "black",
      "text-halo-width": 2,
    },
  } as mapboxgl.SymbolLayer,
];

// Source styles come last so they render on top of cables.
const dropDrawStyles: MapboxStyleLayer[] = [...dropAssignmentStyles, ...sourceStyles];

export default dropDrawStyles;
